/**
 * Admin moderation queue. Reports prioritized by createdAt + the
 * `relatedReportCount` enrichment from the backend (more open reports
 * against the same reported user = higher urgency).
 *
 * Flow: claim a report → review the reason + note + context →
 * resolve with dismiss / warn / soft_pause / ban + an optional admin
 * note (audit-logged either way).
 *
 * Senior-moderator+ only sees the `ban` action enabled in resolution;
 * lower roles can still click it but the backend returns 403 and we
 * surface the error inline. Plan §Admin Roles & Permissions has the
 * full role matrix.
 */

import { Badge } from '@funkihoo/ui-web';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type ReportRow,
  useClaimReportMutation,
  useQueueQuery,
  useResolveReportMutation,
} from '../api/moderation.js';

type ResolutionKind = 'dismiss' | 'warn' | 'soft_pause' | 'ban';
const RESOLUTIONS: { value: ResolutionKind; label: string; danger?: boolean }[] = [
  { value: 'dismiss', label: 'Dismiss' },
  { value: 'warn', label: 'Warn' },
  { value: 'soft_pause', label: 'Soft pause' },
  { value: 'ban', label: 'Ban', danger: true },
];

export function ModerationQueueRoute() {
  const queue = useQueueQuery();
  const claim = useClaimReportMutation();
  const [activeReportId, setActiveReportId] = useState<string | null>(null);

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">Moderation queue</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Open reports, oldest first within the same-user cluster. Claim before reviewing; that
          locks the row to your admin id so two of us aren't racing on the same case. Every claim +
          resolution is audit-logged.
        </p>
      </header>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => queue.refetch()}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          Refresh
        </button>
        {queue.isFetching && <span className="text-xs text-neutral-500">refreshing…</span>}
        {queue.data && (
          <span className="text-sm text-neutral-600">
            {queue.data.reports.length} open report{queue.data.reports.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {queue.isLoading && <p className="text-sm text-neutral-500">Loading queue…</p>}
      {queue.isError && (
        <p className="text-sm text-danger-600">
          Could not load the queue. Backend down? Check moderation-service on :8008.
        </p>
      )}
      {queue.data && queue.data.reports.length === 0 && (
        <p className="text-sm text-neutral-500">No open reports. Quiet day.</p>
      )}

      {queue.data && queue.data.reports.length > 0 && (
        <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
          {queue.data.reports.map((r) => (
            <li key={r.id}>
              <ReportRowItem
                row={r}
                expanded={activeReportId === r.id}
                onToggle={() => setActiveReportId((prev) => (prev === r.id ? null : r.id))}
                onClaim={() => claim.mutate(r.id)}
                claimPending={claim.isPending && claim.variables === r.id}
                claimError={claim.error}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface ReportRowItemProps {
  row: ReportRow;
  expanded: boolean;
  onToggle: () => void;
  onClaim: () => void;
  claimPending: boolean;
  claimError: Error | null;
}

function ReportRowItem({
  row,
  expanded,
  onToggle,
  onClaim,
  claimPending,
  claimError,
}: ReportRowItemProps) {
  return (
    <div className="px-3 py-3 hover:bg-neutral-50">
      {/* The title row is the expand/collapse toggle. The meta row
       *  below it sits outside the <button> because it contains
       *  <Link>s to the reporter + reported users' dossiers — an
       *  <a> inside a <button> is invalid HTML and most browsers
       *  drop the nav. */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between text-left"
      >
        <p className="text-sm font-medium text-neutral-900">
          {row.reason}
          {row.relatedReportCount > 1 && (
            <Badge variant="warning" density="compact" className="ml-2">
              {row.relatedReportCount} related
            </Badge>
          )}
          {row.claimedByAdminId && (
            <Badge variant="info" density="compact" className="ml-2">
              claimed
            </Badge>
          )}
        </p>
        <span className="text-xs text-neutral-400">{expanded ? '−' : '+'}</span>
      </button>
      <p className="mt-0.5 text-xs text-neutral-500">
        Filed {new Date(row.createdAt).toLocaleString()} · reporter{' '}
        <Link
          to={`/users?id=${row.reporterUserId}`}
          className="font-mono text-primary-700 hover:underline"
        >
          {row.reporterUserId.slice(0, 8)}
        </Link>{' '}
        · reported{' '}
        <Link
          to={`/users?id=${row.reportedUserId}`}
          className="font-mono text-primary-700 hover:underline"
        >
          {row.reportedUserId.slice(0, 8)}
        </Link>
      </p>

      {expanded && (
        <div className="mt-3 space-y-3 rounded-md border border-neutral-200 bg-white p-3">
          {row.note && (
            <p className="text-sm text-neutral-700">
              <span className="font-medium text-neutral-900">Reporter note:</span> {row.note}
            </p>
          )}
          {!row.claimedByAdminId && (
            <div>
              <button
                type="button"
                onClick={onClaim}
                disabled={claimPending}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {claimPending ? 'Claiming…' : 'Claim'}
              </button>
              {claimError && <p className="mt-1 text-sm text-danger-600">{claimError.message}</p>}
            </div>
          )}
          {row.claimedByAdminId && <ResolveForm reportId={row.id} />}
        </div>
      )}
    </div>
  );
}

function ResolveForm({ reportId }: { reportId: string }) {
  const [resolution, setResolution] = useState<ResolutionKind>('dismiss');
  const [note, setNote] = useState('');
  const resolve = useResolveReportMutation();

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        const body: { reportId: string; resolution: ResolutionKind; note?: string } = {
          reportId,
          resolution,
        };
        if (note.trim().length > 0) {
          body.note = note.trim();
        }
        resolve.mutate(body);
      }}
    >
      <fieldset className="flex flex-wrap gap-2">
        <legend className="text-sm font-medium text-neutral-700">Resolution</legend>
        {RESOLUTIONS.map((r) => (
          <label
            key={r.value}
            className={`inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
              resolution === r.value
                ? r.danger
                  ? 'border-danger-500 bg-danger-50 text-danger-700'
                  : 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            <input
              type="radio"
              name={`resolution-${reportId}`}
              value={r.value}
              checked={resolution === r.value}
              onChange={() => setResolution(r.value)}
              className="sr-only"
            />
            {r.label}
          </label>
        ))}
      </fieldset>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional admin note (audit-logged)"
        rows={2}
        className="w-full rounded-md border border-neutral-300 p-2 text-sm"
      />
      {resolve.error && (
        <p className="text-sm text-danger-600">{(resolve.error as Error).message}</p>
      )}
      {resolve.data && <p className="text-sm text-success-700">Resolved: {resolve.data.status}</p>}
      <button
        type="submit"
        disabled={resolve.isPending}
        className={`rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
          resolution === 'ban'
            ? 'bg-danger-600 hover:bg-danger-700'
            : 'bg-primary-600 hover:bg-primary-700'
        }`}
      >
        {resolve.isPending ? 'Submitting…' : 'Resolve'}
      </button>
    </form>
  );
}
